import { hasRole } from '@heyjob-dash/shared/utils/check-permission';
import { autoAvailable } from '@heyjob-dash/shared/fringe-auto/auto-feature';
import { hasTimesheetClockings } from '@heyjob-dash/shared/data/timesheetTypes';
import { MODULE_FLAGS, isModuleEnabled, getCurrentCompany } from '@heyjob-dash/shared/config/company-modules';

import { paths } from 'src/routes/paths';

import Label from 'src/components/label';

import Iconify from '../../components/iconify';

const navIcon = (name) => (
  <Iconify sx={{ width: 24, height: 24 }} icon={`solar:${name}-bold-duotone`} />
);

function section(subheader, items) {
  return { subheader, items: items.filter(Boolean) };
}

// Menu dello Studio con un'azienda selezionata: stesse sezioni e icone del menu V2 del portale Azienda.
// Vale per tutti, senza il flag company_menu_v2. Rotte e voci sono quelle dello Studio.
export function buildStudioCompanyNav({ user, t }) {
  const companyId = user?.data?.current_company;
  const company = getCurrentCompany(user);
  const module = (flag) => isModuleEnabled(company, flag);
  const workspaceRole = hasRole(user, ['Studio', 'Operatore']) && company;

  const attendanceChildren = [
    module(MODULE_FLAGS.TIMESHEET) && {
      title: 'Cartellini',
      path: paths.company.timesheet,
    },
    workspaceRole && company.feature_requests && {
      title: t('requests'),
      path: paths.company.requests,
    },
    workspaceRole && company.feature_holiday_plan && {
      title: <span style={{ textTransform: 'none' }}>{t('holiday_plan')}</span>,
      path: paths.company.holidayPlan,
    },
  ].filter(Boolean);

  const documentChildren = [
    module(MODULE_FLAGS.DOCUMENTS) && {
      title: 'Documenti azienda',
      path: paths.company.documents,
    },
    workspaceRole && module(MODULE_FLAGS.DOCUMENTS) && (module(MODULE_FLAGS.DOCUMENT_GENERATION) || module(MODULE_FLAGS.SIGNATURES)) && {
      title: <span style={{ textTransform: 'none' }}>Invii e firme</span>,
      path: paths.company.documentsSignatures,
    },
    module(MODULE_FLAGS.DOCUMENTS) && {
      title: 'Buste paga',
      path: paths.dashboard.uploadPayslips,
    },
  ].filter(Boolean);

  // Dati contabili e Trasparenza retributiva non dipendono dal modulo Report: lo Studio li ha anche con Report spento.
  const reportChildren = [
    module(MODULE_FLAGS.REPORTS) && module(MODULE_FLAGS.TIMESHEET) && company?.timesheet_types?.some(hasTimesheetClockings) && {
      title: 'Timbrature',
      path: paths.dashboard.clockings,
    },
    module(MODULE_FLAGS.REPORTS) && module(MODULE_FLAGS.TIMESHEET) && {
      title: t('timesheet'),
      path: paths.company.stats,
    },
    module(MODULE_FLAGS.PAYROLL_DATA) && {
      title: 'Dati contabili',
      path: paths.company.payrollData,
    },
    module(MODULE_FLAGS.REPORTS) && {
      title: 'Residui dipendenti',
      path: paths.company.employeesPayrollData,
    },
    module(MODULE_FLAGS.REPORTS) && module(MODULE_FLAGS.DOCUMENTS) && {
      title: 'Accesso documenti',
      path: paths.company.employeesDocuments,
    },
    {
      title: 'Trasparenza retributiva',
      path: paths.company.payTransparency,
    },
  ].filter(Boolean);

  const configChildren = [
    {
      title: t('custom_fields'),
      path: paths.company.config.customFields,
    },
    module(MODULE_FLAGS.TIMESHEET) && {
      title: t('timesheet'),
      path: paths.company.config.timesheet,
    },
    module(MODULE_FLAGS.TIMESHEET) && {
      title: t('clocking places'),
      path: paths.company.config.clockingPlaces,
    },
    company?.feature_expense_reports && {
      title: t('expense_reports'),
      path: paths.company.config.expenseReports,
    },
    {
      title: t('supervisors'),
      path: paths.company.config.supervisors,
    },
    {
      title: t('areas'),
      path: paths.company.config.areas,
    },
    module(MODULE_FLAGS.DOCUMENTS) && module(MODULE_FLAGS.DOCUMENT_GENERATION) && {
      title: t('document_templates'),
      path: paths.company.config.documentTemplates,
    },
    workspaceRole && company.feature_holiday_plan && {
      title: <span style={{ textTransform: 'none' }}>{t('holiday_plan')}</span>,
      path: paths.company.config.holidayPlan,
    },
    workspaceRole && module(MODULE_FLAGS.PROJECTS) && {
      title: t('projects'),
      path: paths.company.config.projects,
    },
  ].filter(Boolean);

  return [
    section('Panoramica', [
      {
        title: t('company'),
        path: paths.dashboard.companyDetail(companyId),
        icon: navIcon('buildings-2'),
      },
      {
        title: t('employees'),
        path: paths.company.employees,
        icon: navIcon('users-group-rounded'),
      },
    ]),
    section('Operatività', [
      attendanceChildren.length > 0 && {
        title: t('attendance'),
        path: attendanceChildren[0].path,
        icon: navIcon('calendar-mark'),
        children: attendanceChildren,
      },
      module(MODULE_FLAGS.DEADLINES) && {
        title: t('timetable'),
        path: paths.company.calendar,
        icon: navIcon('calendar'),
      },
      company?.feature_expense_reports && {
        title: t('expense_reports'),
        path: paths.company.expenseReports,
        icon: navIcon('wallet-money'),
      },
      autoAvailable(user, ['Studio', 'Operatore']) && { title: 'Auto', path: paths.dashboard.auto, icon: navIcon('wheel') },
      documentChildren.length > 0 && {
        title: t('documents'),
        path: documentChildren[0].path,
        icon: navIcon('folder-with-files'),
        children: documentChildren,
      },
      {
        title: 'Report',
        path: reportChildren[0].path,
        icon: navIcon('chart-square'),
        children: reportChildren,
      },
    ]),
    section('Sistema', [
      {
        title: t('config'),
        path: configChildren[0].path,
        icon: navIcon('settings'),
        children: configChildren,
      },
      {
        title: 'Integrazioni',
        path: '#',
        icon: navIcon('plug-circle'),
        info: <Label color="warning" variant="soft">ALPHA</Label>,
        children: [
          { title: 'B.Point', path: paths.dashboard.integrations.bpoint },
          { title: 'Zucchetti', path: paths.dashboard.integrations.zucchettiExport },
        ],
      },
    ]),
  ].filter((group) => group.items.length > 0);
}
